import {Tooltip, TooltipProps} from '@mui/material';
import {ReactElement, ReactNode, RefObject, useEffect, useRef, useState} from 'react';

import {checkLineClampOverflow} from 'utils/checkOverflow';

type OverflownTooltipProps<T extends HTMLElement> = {
  children: (ref: RefObject<T | null>, isOverflown: boolean) => ReactElement;
  title: ReactNode;
  /** You probably need div around LineClamp to make this work properly */
  lineClamp?: number;
} & Omit<TooltipProps, 'title' | 'ref' | 'children'>;

export const OverflownTooltip = <T extends HTMLElement>({
  children,
  title,
  lineClamp = 1,
  ...tooltipProps
}: OverflownTooltipProps<T>) => {
  const ref = useRef<T>(null);
  const [isOverflown, setIsOverflown] = useState(false);

  useEffect(() => {
    const checkNodeOverflow = () => {
      if (ref.current && title) {
        setIsOverflown(checkLineClampOverflow(ref.current, lineClamp));
      }
    };

    checkNodeOverflow();
    window.addEventListener('resize', checkNodeOverflow);

    return () => {
      window.removeEventListener('resize', checkNodeOverflow);
    };
  }, [ref, title, lineClamp]);

  return (
    <Tooltip title={isOverflown ? title : undefined} {...tooltipProps}>
      {children(ref, isOverflown)}
    </Tooltip>
  );
};
